/**
 * TABLE DATA HOOK
 * Generic data management for universal table
 * @version 1.0.0
 * @production-ready
 */

import { useState, useCallback, useMemo } from 'react';
import { TableData, ColumnDefinition } from '../types';
import { parseCellValue, validateCellValue } from '../utils';

export interface UseTableDataReturn {
  data: TableData;
  rowKeys: string[];
  updateCell: (rowKey: string, columnKey: string, value: any) => void;
  updateRow: (rowKey: string, rowData: any) => void;
  addRow: (rowKey: string, initialData?: any) => void;
  removeRow: (rowKey: string) => void;
  updateRowKey: (oldKey: string, newKey: string) => void;
  clearData: () => void;
  setAllData: (newData: TableData, newRowKeys: string[]) => void;
  getCellValue: (rowKey: string, columnKey: string) => any;
  getRowData: (rowKey: string) => any;
  validateCell: (rowKey: string, columnKey: string, column: ColumnDefinition) => boolean | string;
  sortRows: (compareFn: (a: string, b: string) => number) => void;
}

export const useTableData = (
  initialData: TableData = {},
  initialRowKeys: string[] = []
): UseTableDataReturn => {
  const [data, setData] = useState<TableData>(initialData);
  const [rowKeys, setRowKeys] = useState<string[]>(initialRowKeys);

  /**
   * Update single cell value
   */
  const updateCell = useCallback((rowKey: string, columnKey: string, value: any) => {
    setData(prev => ({
      ...prev,
      [rowKey]: {
        ...prev[rowKey],
        [columnKey]: value
      }
    }));
  }, []);

  /**
   * Update entire row
   */
  const updateRow = useCallback((rowKey: string, rowData: any) => {
    setData(prev => ({
      ...prev,
      [rowKey]: { ...prev[rowKey], ...rowData }
    }));
  }, []);

  /**
   * Add new row
   */
  const addRow = useCallback((rowKey: string, initialData?: any) => {
    if (rowKeys.includes(rowKey)) {
return;
    }

    setRowKeys(prev => [...prev, rowKey]);
    setData(prev => ({
      ...prev,
      [rowKey]: initialData || {}
    }));
  }, [rowKeys]);

  /**
   * Remove row
   */
  const removeRow = useCallback((rowKey: string) => {
    setRowKeys(prev => prev.filter(key => key !== rowKey));
    setData(prev => {
      const newData = { ...prev };
      delete newData[rowKey];
      return newData;
    });
  }, []);

  /**
   * Update row key (e.g., change date)
   */
  const updateRowKey = useCallback((oldKey: string, newKey: string) => {
    if (oldKey === newKey) return;
    if (rowKeys.includes(newKey)) {
return;
    }

    setRowKeys(prev => prev.map(key => key === oldKey ? newKey : key));
    setData(prev => {
      const newData = { ...prev };
      newData[newKey] = newData[oldKey];
      delete newData[oldKey];
      return newData;
    });
  }, [rowKeys]);

  /**
   * Clear all data
   */
  const clearData = useCallback(() => {
    setData({});
    setRowKeys([]);
  }, []);

  /**
   * Set all data at once
   */
  const setAllData = useCallback((newData: TableData, newRowKeys: string[]) => {
    setData(newData);
    setRowKeys(newRowKeys);
  }, []);

  /**
   * Get cell value
   */
  const getCellValue = useCallback((rowKey: string, columnKey: string): any => {
    return data[rowKey]?.[columnKey];
  }, [data]);

  /**
   * Get entire row data
   */
  const getRowData = useCallback((rowKey: string): any => {
    return data[rowKey] || {};
  }, [data]);

  /**
   * Validate cell value
   */
  const validateCell = useCallback((
    rowKey: string,
    columnKey: string,
    column: ColumnDefinition
  ): boolean | string => {
    const value = getCellValue(rowKey, columnKey);
    const rowData = getRowData(rowKey);
    return validateCellValue(value, column, rowData);
  }, [getCellValue, getRowData]);

  /**
   * Sort rows by custom comparator
   */
  const sortRows = useCallback((compareFn: (a: string, b: string) => number) => {
    setRowKeys(prev => [...prev].sort(compareFn));
  }, []);

  return {
    data,
    rowKeys,
    updateCell,
    updateRow,
    addRow,
    removeRow,
    updateRowKey,
    clearData,
    setAllData,
    getCellValue,
    getRowData,
    validateCell,
    sortRows
  };
};

/**
 * Hook for calculated columns
 */
export const useCalculatedColumns = (
  data: TableData,
  rowKeys: string[],
  columns: ColumnDefinition[]
): TableData => {
  return useMemo(() => {
    const calculatedData = { ...data };

    rowKeys.forEach(rowKey => {
      columns.forEach(column => {
        if (column.calculated && column.calculateFn) {
          const rowData = calculatedData[rowKey] || {};
          const calculatedValue = column.calculateFn(rowData, calculatedData);
          calculatedData[rowKey] = {
            ...calculatedData[rowKey],
            [column.key]: calculatedValue
          };
        }
      });
    });

    return calculatedData;
  }, [data, rowKeys, columns]);
};

